import {ParsedUrlQuery} from 'querystring';
import {TranslationFunction} from 'translations';
import {
  IProduct,
  IProductByNameResponse,
  IProductsResponse,
  IRestaurantResponse,
  IResultProducts,
  ISpecialProduct,
} from 'types';

import logger from './logger';

export type CrossProduct = {
  id: number;
  name: string;
  price: number;
};

export type CrossSellingSections = {
  name: string;
  products: IProduct[];
};

export const getNormalizedCrossSelling = (productsData?: IProductsResponse, categoryId?: number) => {
  if (!productsData?.products || !categoryId) return [];
  const productCategory = productsData.categories.find((category) => category.id === categoryId);
  if (!productCategory || !productCategory?.cross_selling_products) return [];
  const cross = productCategory.cross_selling_products;
  const finalProd: IProduct[] = [];
  const categoryKeys: string[] = [];
  // cast products from crossSelling to menuType
  for (let index = 0; index < cross.length; index++) {
    const crossItem = cross[index];
    const menuProduct = productsData.products;
    const filteredMenuItems = menuProduct.find((menuItem) => menuItem.id === crossItem.id);
    if (!filteredMenuItems) continue;
    finalProd.push(filteredMenuItems);
    if (filteredMenuItems.menu_category) {
      if (!categoryKeys.includes(filteredMenuItems.menu_category.name))
        categoryKeys.push(filteredMenuItems.menu_category?.name);
    }
  }

  // split products in categories || same as glovo ))
  const crossSellingSections: CrossSellingSections[] = [];
  for (let index = 0; index < categoryKeys.length; index++) {
    const category = categoryKeys[index];
    const categoryProducts = finalProd.filter((prod) => prod.menu_category?.name === category);
    crossSellingSections.push({name: category, products: categoryProducts});
  }
  return crossSellingSections;
};

/**
|--------------------------------------------------
| [SLUG] - provider search
|--------------------------------------------------
*/

export const filterSearch = (productsCategories: IResultProducts[], query: string | string[]) => {
  const lowercasedQuery = `${query}`.toLowerCase();
  const bySearch: IResultProducts[] = [];

  productsCategories.map((item) => {
    const filtered = item.products.filter((item) => item.name.toLowerCase().includes(lowercasedQuery.toLowerCase()));
    if (filtered.length) {
      bySearch.push({...item, products: filtered});
    }
  });

  return bySearch;
};

export const prepareCategoriesWithProducts = (
  productsData: IProductsResponse | undefined,
  providerData: IRestaurantResponse | undefined,
  t: TranslationFunction,
) => {
  if (!productsData || !providerData) return [];

  const {products, categories} = productsData;
  const isRecommendedProduct = !!providerData?.recommended_products.length;

  const categoriesWithProducts: IResultProducts[] = [];
  const currentCategories = categories;

  if (products) {
    for (const menuItem of currentCategories) {
      const categoryProduct = products.filter((item) => item.menu_category.id === menuItem.id);
      categoriesWithProducts.push({
        id: menuItem.id,
        name: menuItem.name,
        products: categoryProduct,
      });
    }
  }
  if (isRecommendedProduct) {
    categoriesWithProducts.unshift({
      id: 0,
      name: `${t('RECOMMENDED_PRODUCT')}`,
      products: providerData?.recommended_products,
    });
  }

  return categoriesWithProducts;
};

export const getCorrectProviderSlug = (
  query: ParsedUrlQuery,
  slugArr: string[],
  providerSlugResponse: IProductByNameResponse | undefined,
) => {
  let tempProvider = query.previewId
    ? query.previewId
    : slugArr.length === 1
    ? slugArr[slugArr.length - 1]
    : providerSlugResponse?.provider_slug;

  logger.addBreadcrumb('slug-getCorrectProviderSlug', {
    providerSlug: tempProvider,
    query: query,
  });
  if (tempProvider?.includes('#')) {
    const fixedSlug = `${tempProvider}`?.split('#')[0];
    logger.logDebug(`${fixedSlug}`, 'slug');
    tempProvider = fixedSlug;
    logger.addBreadcrumb('slug-fix#', {
      providerSlug: tempProvider,
      query: query,
    });
  }

  return tempProvider as string | undefined;
};

export const mergeProductsByProviderSlug = (data: ISpecialProduct[], providerSlug: string) => {
  const filteredProducts = data.filter((product) => product.provider_slug === providerSlug);
  return filteredProducts.length > 0 ? {name: providerSlug, products: filteredProducts} : null;
};
